/* =============================================================================
   src/utils/qr-code.js — SVG-QR-Code-Generator (Phase 53, Session 54)
   =============================================================================
   Zentrale Wrapper-Schicht über `qrcode-generator` (npm). Liefert scannbare
   SVG-QR-Codes für:
     - TSE-Bon-QR (BSI TR-03153 Payload aus tse_qr_payload)
     - Tisch-QR für Online-Bestellung (geplante Phase)
     - Cancel-Link-QR in Reservierungs-Mails (geplante Phase)

   Single-Source-Pattern (Memory `feedback_gg_hard_way`): EINE Lib für alle
   QR-Use-Cases, gleiche API, gleiche Optik.

   Setup:
     npm install qrcode-generator     # einmalig, ist in package.json
     beim Vite-Build wird die Lib in den Bundle gepackt.

   Wenn die Lib nicht installiert ist, fällt `generateQrSvg()` auf ein
   visuelles Fallback-SVG zurück (Hinweis-Box mit Payload-Text), damit
   der Bon trotzdem rendert. Logs eine Warnung.

   API:
     generateQrSvg(payload, opts?) → Promise<string>   // SVG-Markup
     isQrGeneratorAvailable() → Promise<boolean>       // Lib-Check
============================================================================ */

let _qrLibCache = undefined; // null = bekannt-fehlend, function = Lib

/**
 * Lazy-Import von qrcode-generator. Wenn die Lib nicht installiert ist:
 * single warn + null cachen, damit nicht jeder Render-Aufruf erneut probt.
 */
async function loadQrLib() {
  if (_qrLibCache !== undefined) return _qrLibCache;
  try {
    const mod = await import('qrcode-generator');
    // qrcode-generator exportiert die Factory als Default-Export.
    _qrLibCache = mod.default || mod;
    return _qrLibCache;
  } catch (err) {
    console.warn(
      '[qr-code] qrcode-generator nicht verfügbar. Bitte einmal '
      + '`npm install qrcode-generator` ausführen. Fallback-Hinweisbox '
      + 'wird gerendert. Originalfehler:', (err && err.message) || err
    );
    _qrLibCache = null;
    return null;
  }
}

/** Test-Hook: erlaubt Smoke-Suite, den Cache zurückzusetzen. */
export function _resetQrLibCache() {
  _qrLibCache = undefined;
}

/** Liefert true, wenn die Lib geladen werden konnte. Async damit lazy. */
export async function isQrGeneratorAvailable() {
  return (await loadQrLib()) !== null;
}

/**
 * Generiert ein scannbares SVG für den übergebenen String.
 *
 * @param {string} payload  Der zu kodierende String. Pflichtfeld.
 * @param {object} [opts]
 * @param {number} [opts.cellSize=4]     Pixel pro QR-Modul. 4 = ~25mm bei 150 DPI.
 * @param {number} [opts.margin=2]       Quiet-Zone in Modulen. KassenSichV/BSI: ≥ 4 ideal.
 * @param {string} [opts.errorLevel='M'] L | M | Q | H. Höher = besser scannbar, größer.
 * @param {string} [opts.color='#111']   QR-Pixel-Farbe.
 * @param {string} [opts.background='#fff'] Hintergrund. Weiß = beste Scan-Rate.
 * @param {string} [opts.ariaLabel]      ARIA-Label. Default: 'TSE-QR-Code'.
 * @returns {Promise<string>} SVG-Markup-String.
 */
export async function generateQrSvg(payload, opts = {}) {
  const cellSize    = Number.isFinite(opts.cellSize) ? opts.cellSize : 4;
  const margin      = Number.isFinite(opts.margin)   ? opts.margin   : 2;
  const errorLevel  = (opts.errorLevel || 'M').toUpperCase();
  const color       = opts.color      || '#111';
  const background  = opts.background || '#fff';
  const ariaLabel   = opts.ariaLabel  || 'TSE-QR-Code';

  if (typeof payload !== 'string' || payload.length === 0) {
    return _fallbackSvg('Kein QR-Payload', '#777', background, ariaLabel);
  }

  const qrLib = await loadQrLib();
  if (!qrLib) {
    return _fallbackSvg(
      'QR-Library fehlt — npm install qrcode-generator',
      '#a00', background, ariaLabel
    );
  }

  try {
    // qrcode-generator API: qrcode(typeNumber, errorCorrectionLevel)
    // typeNumber=0 = Auto-Select. errorCorrectionLevel 'L'|'M'|'Q'|'H'.
    const qr = qrLib(0, errorLevel);
    qr.addData(payload);
    qr.make();

    const moduleCount = qr.getModuleCount();
    const size = (moduleCount + margin * 2) * cellSize;

    // S240-HF7.4 — EIN <path> für alle Module statt eines <rect> je Modul.
    //
    // Der alte Weg („overkill für ~30×30 Module") war in der Praxis teuer: der
    // TSE-Payload braucht eine große QR-Version, gemessen **2.761 <rect>** pro
    // Bon → der archivierte Bon-Snapshot wog Ø 116 kB, das Bon-Archiv insgesamt
    // 22,9 MB. Ein Pfad mit „Move + horizontales Rechteck" je Modul kodiert
    // dieselben Pixel in einem Bruchteil der Bytes.
    //
    // Optik bleibt IDENTISCH: gleiche Modulgröße, gleiche Quiet-Zone, gleiche
    // Koordinaten, gleiches `shape-rendering="crispEdges"`. Waagerecht
    // benachbarte Module werden zu EINEM Rechteck zusammengefasst (run-length) —
    // das ändert nichts am gerasterten Bild, spart aber nochmal deutlich.
    //
    // Auch der Hintergrund ist ein <path> (kein <rect>): er bleibt explizit
    // gemalt, damit Druck-Raster und PDF-Konvertierung eine weiße Fläche unter
    // dem Code haben und nicht auf eine CSS-Hintergrundfarbe angewiesen sind.
    // Die viewBox rechnet in MODUL-Einheiten (nicht in Pixeln): dadurch sind die
    // Pfad-Koordinaten ein- bis zweistellig statt drei- bis vierstellig — nochmal
    // ~40 % weniger Bytes. `width`/`height` bleiben in Pixeln, der Skalierungs-
    // faktor ist exakt `cellSize` (ganzzahlig) → mit `crispEdges` pixelgenau
    // dasselbe Bild wie vorher.
    const units = moduleCount + margin * 2;
    const parts = [];
    for (let row = 0; row < moduleCount; row++) {
      let runStart = -1;
      for (let col = 0; col <= moduleCount; col++) {
        const dark = col < moduleCount && qr.isDark(row, col);
        if (dark && runStart < 0) runStart = col;
        if (!dark && runStart >= 0) {
          const x = runStart + margin;
          const y = row + margin;
          parts.push(`M${x} ${y}h${col - runStart}v1H${x}z`);
          runStart = -1;
        }
      }
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${units} ${units}" `
      + `width="${size}" height="${size}" role="img" aria-label="${_escAttr(ariaLabel)}" `
      + `class="qr-code-svg" shape-rendering="crispEdges">`
      + `<path class="qr-bg" fill="${_escAttr(background)}" d="M0 0h${units}v${units}H0z"/>`
      + `<path class="qr-modules" fill="${_escAttr(color)}" d="${parts.join('')}"/>`
      + `</svg>`;
  } catch (err) {
    console.error('[qr-code] QR-Generierung fehlgeschlagen:', err);
    return _fallbackSvg(
      'QR-Generierung fehlgeschlagen',
      '#a00', background, ariaLabel
    );
  }
}

/** Kleines Fallback-SVG mit Hinweis-Text. Damit der Bon nicht völlig leer ist. */
function _fallbackSvg(msg, color, background, ariaLabel) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 80" `
    + `width="200" height="80" role="img" aria-label="${_escAttr(ariaLabel)}" `
    + `class="qr-code-svg qr-code-fallback">`
    + `<rect width="100%" height="100%" fill="${_escAttr(background)}" stroke="${_escAttr(color)}" stroke-width="1"/>`
    + `<text x="100" y="44" text-anchor="middle" font-family="sans-serif" `
    + `font-size="10" fill="${_escAttr(color)}">${_escText(msg)}</text>`
    + `</svg>`;
}

function _escAttr(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[c]);
}

function _escText(s) {
  return String(s).replace(/[&<>]/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;'
  })[c]);
}
